import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import CommonSpinner from "@widgets/CommonSpinner";
import SharedForm from "@widgets/SharedForm";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { dateToString } from "@shared/utils/DateUtils";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import SnButton from "@shared/components/SnButton";

interface popupProps {
  selectedData?: any;
  modalRef?: any;
}

const SN_PRD_WRK_REG_001_POP_001 = forwardRef(
  ({ selectedData, modalRef }: popupProps, ref: React.Ref<any>) => {
    const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
      grid1: React.createRef<SnGridRef>(),
      grid2: React.createRef<SnGridRef>(),
    });

    const getGridCurrent = (key: string): SnGridRef => {
      const grid = gridRefs.current[key]?.current;
      if (!grid) {
        throw new Error(`Grid '${key}' is not mounted yet`);
      }
      return grid;
    };

    const { userInfo } = global(); // Login user 정보
    const SharedFormref1 = useRef<any>(null);
    const SharedFormref2 = useRef<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [formData, setFormData] = useState<any>();
    const [form1Data, setForm1Data] = useState<any>();
    const [formControlData, setFormControlData] = useState<any>({
      form1: [],
      form2: [],
    });
    const [columns, setColums] = useState<any>({
      grid1: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
    });

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_PRD_WRK_REG_001_POP_001",
            {}
          );
          const mergedData: any = {
            ...(rtn.form?.form1ControlData || {}),
            ...selectedData,
            dt: dateToString(new Date()),
          };
          setFormControlData({
            form1: rtn.form?.form1 || [],
            form2: rtn.form?.form2 || [],
          });
          setFormData(mergedData);
          setForm1Data(mergedData);
          setColums({
            grid1: rtn.grid.grid1,
          });

          // 화면 오픈 시 비가동내역 조회 (state 반영 전이므로 조회조건을 직접 넘긴다)
          await onSearch(mergedData);
        } catch (error) {}
      };
      fetchform();
    }, []);

    //#region onSearch
    const onSearch = async (searchData?: any) => {
      setIsLoading(true);
      try {
        const cond: any = searchData ?? formData ?? {};
        const param = {
          id: "sn_prd_wrk_reg_001_pop_001_01",
          params: {
            cd_firm: userInfo.cd_firm,
            fg_resource: "1",
            cd_resource: cond.cd_equip,
            dt: cond.dt,
          },
        };

        const data: any = await fnGetDataSet(param);
        setDataSources({
          grid1: data[0],
        });
      } catch (error: any) {
      } finally {
        setIsLoading(false);
      }
    };
    //#endregion

    //#region onSave
    const onSave = async () => {
      try {
        const downtimeChanges = getGridCurrent("grid1").getChange();

        const SaveData = {
          downtime_act: downtimeChanges,
        };

        const ok = await fnSave("SN_PRD_WRK_REG_001_POP_001_SAVE_01", SaveData);
        if (!ok) return false;

        getGridCurrent("grid1").resetUpdate();
        return true;
      } catch (error: any) {
        getGridCurrent("grid1").resetUpdate(true);
        return false;
      }
    };
    //#endregion

    //#region Grid Events
    const onBeforeAddRow = (): any => {
      try {
        const maxNoLine = getGridCurrent("grid1").getMaxValue("no_line");
        // 지시정보 폼(form1) / 비가동등록 폼(form2)의 현재 입력값
        const form1: any = SharedFormref1.current?.getFormdata?.() || {};
        const form2: any = SharedFormref2.current?.getFormdata?.() || {};
        const updatedRow = {
          cd_firm: userInfo?.cd_firm,
          fg_resource: "1",
          no_line: maxNoLine + 1,
          dt: form2.dt,
          cd_resource: form2.cd_equip,
          tm_start: form2.tm_from,
          tm_end: form2.tm_to,
          qt_down: form2.qt_down ?? 0,
          cd_downtime: form2.cd_downtime,
          dc_downtime: form2.dc_downtime,
          no_wo: form1.no_wo ?? null,
          no_line_wo: form1.no_line ?? form1.no_line_wo ?? 0,
        };

        return updatedRow;
      } catch (error: any) {
        return false; // 추가 취소
      }
    };
    //#endregion

    //#region 정지시간 -> 분 계산
    // "HH:mm:ss" / "HHmmss" -> 분(소수 포함)
    const timeToMinutes = (raw: any): number | null => {
      const digits = (raw ?? "").toString().replace(/[^0-9]/g, "");
      if (digits.length < 4) return null;
      const hh = Number(digits.slice(0, 2));
      const mm = Number(digits.slice(2, 4));
      const ss = digits.length >= 6 ? Number(digits.slice(4, 6)) : 0;
      return hh * 60 + mm + ss / 60;
    };

    // 종료 - 시작 을 분으로 계산한다. 종료가 시작보다 작으면 익일로 간주한다.
    const calcDownMinutes = (tmFrom: any, tmTo: any): number => {
      const start = timeToMinutes(tmFrom);
      const end = timeToMinutes(tmTo);
      if (start === null || end === null) return 0;
      let diff = end - start;
      if (diff < 0) diff += 24 * 60;
      return Math.round(diff);
    };

    // 폼2 값 변경. 정지시간(SnPeriodTimeBox, cd_param "tm_from,tm_to")이 바뀌면
    // data = { start, end } 로 들어오므로 qt_down 을 계산해 돌려준다.
    // 반환한 객체는 SharedForm 이 formData 에 합쳐 화면에 반영한다.
    const onForm2ValueChanged = async (
      data: any,
      param: string,
      prevFormData: any
    ) => {
      if (param.includes(",") && data && typeof data === "object") {
        return { qt_down: calcDownMinutes(data.start, data.end) };
      }

      // 일자, 설비값 바뀌면 재조회
      if (param === "dt" || param === "cd_equip") {
        await onSearch({ ...(prevFormData || {}), [param]: data });
      }
      return {};
    };
    //#endregion

    //#region
    const onForm1ButtonClick = (sender: string) => {
      switch (sender) {
        case "btn_clear":
          setForm1Data({});
          break;
      }
    };
    //#endregion

    //#region onSnButtonClick
    const onSnButtonClick = async (id: string) => {
      switch (id) {
        case "Add": {
          if (SharedFormref2.current?.requiredValuesCheck?.() === false) return;

          // 이미 등록된 내역(같은 일자/설비)과 정지시간 구간이 겹치면 추가를 막는다.
          // 끝점이 맞닿는 경우(09:00~10:00, 10:00~11:00)는 겹침으로 보지 않는다.
          {
            const toTimeNum = (raw: any) =>
              Number(
                (raw ?? "")
                  .toString()
                  .replace(/[^0-9]/g, "")
                  .padEnd(6, "0")
              );
            const input: any = SharedFormref2.current?.getFormdata?.() || {};
            const newStart = toTimeNum(input.tm_from);
            const newEnd = toTimeNum(input.tm_to);

            const rows: any[] = [];
            getGridCurrent("grid1").forEachNode((node: any) => {
              if (node?.data) rows.push(node.data);
            });

            const hasInvalid = rows.some(
              (row: any) =>
                row.dt === input.dt &&
                row.cd_resource === input.cd_equip &&
                newStart < toTimeNum(row.tm_end) &&
                newEnd > toTimeNum(row.tm_start)
            );

            if (hasInvalid) {
              await showDialog(
                "alter",
                "이미 등록된 비가동 시간과 겹칩니다.\n정지시간을 확인해 주세요."
              );
              return;
            }
          }

          const addedNode = getGridCurrent("grid1").addRow();
          if (!addedNode) return;

          if (!(await onSave())) {
            getGridCurrent("grid1").deleteSelectedRows();
          }

          // 저장 후 정지시간,정지구분,정지사유 초기화
          const form2: any = SharedFormref2.current?.getFormdata?.() || {};
          setFormData({
            ...form2,
            tm_from: "",
            tm_to: "",
            qt_down: 0,
            cd_downtime: "",
            dc_downtime: "",
          });
          break;
        }
        case "Del":
          const confirm = await showDialog("confirm", "삭제하시겠습니까?");

          if (!confirm) {
            return; // 사용자가 아니오를 선택하면 함수 종료
          }

          getGridCurrent("grid1").deleteSelectedRows();
          await onSave();
          break;
      }
    };
    //#endregion

    //#region useMemo
    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {
            onBeforeAddRow: onBeforeAddRow,
          },
        },
      }),
      [columns, dataSources]
    );
    //#endregion

    return (
      <div className="h-full flex flex-col overflow-hidden">
        <CommonSpinner isLoading={isLoading} />
        <div style={{ height: "150px", marginTop: "10px" }}>
          <SharedForm
            ref={SharedFormref1}
            title={"지시정보"}
            data={form1Data}
            controls={formControlData.form1}
            onButtonClick={onForm1ButtonClick}
          />
        </div>

        <div style={{ height: "150px", marginTop: "10px" }}>
          <SharedForm
            ref={SharedFormref2}
            title={"비가동등록"}
            header={
              <div className="flex justify-end gap-2">
                <SnButton
                  text={"추가"}
                  onClick={() => {
                    onSnButtonClick("Add");
                  }}
                />
              </div>
            }
            data={formData}
            controls={formControlData.form2}
            onValueChaged={onForm2ValueChanged}
          />
        </div>

        <div className="flex-1 min-h-0" style={{ marginTop: "10px" }}>
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "비가동내역",
            }}
            events={grids.grid1.events}
            header={
              <div className="flex justify-end gap-2">
                <SnButton
                  text={"삭제"}
                  onClick={() => {
                    onSnButtonClick("Del");
                  }}
                />
              </div>
            }
          />
        </div>
      </div>
    );
  }
);

export default SN_PRD_WRK_REG_001_POP_001;
